// Action bar for the workbench panel — selected recipe, batch spinner, Queue Craft button.
//
// Extracted from workbench-ui.ts to stay within the 400-line module cap.
// WorkbenchUI creates this, sets onQueueCraft, and calls setRecipe() on selection changes.
// Self-contained rendering: rebuilds its own Container on every state change.

import { Container, Graphics, Text } from "pixi.js";
import { balance } from "./types.ts";
import type { Recipe } from "../../shared/recipes.ts";
import type { InventorySlot } from "../../shared/messages.ts";
import { assessMaterials, type MaterialsResult } from "./workbench-materials.ts";

// -----------------------------------------------------------------------
// Style constants
// -----------------------------------------------------------------------

const PAD = 12; // contracts:allow-literal
const BODY_SIZE = 11; // contracts:allow-literal
const BTN_SIZE = 11; // contracts:allow-literal — button label font size

const ACTION_BAR_BG = 0x0e0e22;
const ACTION_BAR_BORDER = 0x223366;
const BTN_BG = 0x1e3a6a;
const BTN_HOVER = 0x2e4a8a;
const BTN_DISABLED = 0x1a1a2a;
const BTN_BORDER = 0x3355aa;
const BTN_TEXT = 0xeeeeff;
const TEXT_ACTIVE = 0xeeeeff;
const MUTED_COLOR = 0x666688;
const SPIN_BTN_SZ = 22; // contracts:allow-literal — spinner button size px
const QUEUE_BTN_W = 130; // contracts:allow-literal — queue craft button width px
const QUEUE_BTN_H = 28; // contracts:allow-literal — queue craft button height px
const SPIN_OFFSET = 170; // contracts:allow-literal — px from left where spinner starts

// -----------------------------------------------------------------------
// WorkbenchActionBar
// -----------------------------------------------------------------------

export class WorkbenchActionBar {
  private container: Container | null = null;
  private _parent: Container | null = null;
  private _geom: { x: number; y: number; w: number; h: number } | null = null;

  private _recipe: Recipe | null = null;
  private _queuing = false;
  private _batchCount = 1;
  // Phase 7 Block D — inventory snapshot pulled fresh on every render so
  // the queue-craft button reflects the current material gate. Closure
  // means inventoryUpdated lands in clientState and the next rebuild sees it.
  private _inventoryProvider: () => readonly InventorySlot[] = () => [];

  // Wired by WorkbenchUI after network is available.
  onQueueCraft: ((recipeId: string, batchCount: number) => void) | null = null;
  // Phase 7 Block D — fires when the batch spinner mutates _batchCount so
  // the info pane can re-render its per-input have/need rows for the new
  // total (need = inp.count × batchCount).
  onBatchCountChange: (() => void) | null = null;

  build(parent: Container, x: number, y: number, w: number, h: number): void {
    this._parent = parent;
    this._geom = { x, y, w, h };
    this.renderInto(parent, x, y, w, h);
  }

  setRecipe(recipe: Recipe | null): void {
    if (recipe?.id !== this._recipe?.id) this._batchCount = 1; // reset only on recipe change
    this._recipe = recipe;
    this._queuing = false;
    this.rebuild();
  }

  setInventoryProvider(p: () => readonly InventorySlot[]): void {
    this._inventoryProvider = p;
  }

  // Returns the currently-evaluated material gate for the selected
  // recipe + batch count, or null when no recipe is selected. Used by
  // WorkbenchUI to render per-input have/need rows in the info pane.
  currentMaterials(): MaterialsResult | null {
    if (!this._recipe) return null;
    return assessMaterials(this._recipe, this._inventoryProvider(), this._batchCount);
  }

  get batchCount(): number {
    return this._batchCount;
  }

  // Re-render the action bar (and re-evaluate the material gate). Called
  // by WorkbenchUI when inventory changes while the panel is open.
  refresh(): void {
    this.rebuild();
  }

  destroy(): void {
    this.teardown();
    this._parent = null;
    this._geom = null;
  }

  // -----------------------------------------------------------------------
  // Internal rendering
  // -----------------------------------------------------------------------

  private rebuild(): void {
    if (!this._parent || !this._geom) return;
    const { x, y, w, h } = this._geom;
    this.teardown();
    this.renderInto(this._parent, x, y, w, h);
  }

  private teardown(): void {
    if (this.container) {
      this.container.parent?.removeChild(this.container);
      this.container.destroy({ children: true });
      this.container = null;
    }
  }

  private renderInto(parent: Container, x: number, y: number, w: number, h: number): void {
    const c = new Container();
    c.x = x;
    c.y = y;
    parent.addChild(c);
    this.container = c;

    c.addChild(
      new Graphics()
        .rect(0, 0, w, h)
        .fill({ color: ACTION_BAR_BG })
        .stroke({ color: ACTION_BAR_BORDER, width: 1 }), // contracts:allow-literal
    );

    if (!this._recipe) {
      const hint = new Text({
        text: "Select a recipe to queue",
        style: { fontSize: BODY_SIZE, fill: MUTED_COLOR, fontFamily: "monospace", fontStyle: "italic" },
      });
      hint.x = PAD;
      hint.y = Math.round((h - hint.height) / 2); // contracts:allow-literal
      c.addChild(hint);
      return;
    }

    const nameText = new Text({
      text: this._recipe.name,
      style: { fontSize: BODY_SIZE, fill: TEXT_ACTIVE, fontFamily: "monospace", fontWeight: "bold" },
    });
    nameText.x = PAD;
    nameText.y = Math.round((h - nameText.height) / 2); // contracts:allow-literal
    c.addChild(nameText);

    this.renderSpinner(c, h);
    this.renderQueueButton(c, w, h);
  }

  private renderSpinner(parent: Container, barH: number): void {
    const gap = 4; // contracts:allow-literal
    let cx = SPIN_OFFSET;
    const btnY = Math.round((barH - SPIN_BTN_SZ) / 2); // contracts:allow-literal

    const lbl = new Text({
      text: "Qty:",
      style: { fontSize: BODY_SIZE, fill: MUTED_COLOR, fontFamily: "monospace" },
    });
    lbl.x = cx;
    lbl.y = Math.round((barH - lbl.height) / 2); // contracts:allow-literal
    parent.addChild(lbl);
    cx += Math.round(lbl.width) + gap;

    cx =
      this.renderSpinBtn(parent, cx, btnY, "−", () => {
        if (this._batchCount > 1) {
          this._batchCount--;
          this.rebuild();
          this.onBatchCountChange?.();
        }
      }) + gap;

    const countTxt = new Text({
      text: String(this._batchCount),
      style: { fontSize: BODY_SIZE, fill: TEXT_ACTIVE, fontFamily: "monospace", fontWeight: "bold" },
    });
    countTxt.x = cx;
    countTxt.y = Math.round((barH - countTxt.height) / 2); // contracts:allow-literal
    parent.addChild(countTxt);
    cx += 20 + gap; // contracts:allow-literal — fixed width for single/double digit count

    this.renderSpinBtn(parent, cx, btnY, "+", () => {
      if (this._batchCount < balance.crafting.maxQueueDepth) {
        this._batchCount++;
        this.rebuild();
        this.onBatchCountChange?.();
      }
    });
  }

  // Returns the right edge of the button (x + SPIN_BTN_SZ).
  private renderSpinBtn(parent: Container, x: number, y: number, label: string, onClick: () => void): number {
    const c = new Container();
    c.x = x;
    c.y = y;
    c.eventMode = "static";
    c.cursor = "pointer";

    const bg = new Graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      bg
        .roundRect(0, 0, SPIN_BTN_SZ, SPIN_BTN_SZ, 3) // contracts:allow-literal
        .fill({ color: hover ? BTN_HOVER : BTN_BG })
        .stroke({ color: BTN_BORDER, width: 1 }); // contracts:allow-literal
    };
    draw(false);
    c.addChild(bg);

    const t = new Text({ text: label, style: { fontSize: BTN_SIZE, fill: BTN_TEXT, fontFamily: "monospace" } });
    t.x = Math.round((SPIN_BTN_SZ - t.width) / 2); // contracts:allow-literal
    t.y = Math.round((SPIN_BTN_SZ - t.height) / 2); // contracts:allow-literal
    c.addChild(t);

    c.on("pointerover", () => draw(true));
    c.on("pointerout", () => draw(false));
    c.on("pointertap", onClick);

    parent.addChild(c);
    return x + SPIN_BTN_SZ;
  }

  private renderQueueButton(parent: Container, barW: number, barH: number): void {
    // Phase 7 Block D — gate Queue Craft on materials sufficiency. The
    // server still authoritatively rejects (insufficient_materials), but
    // disabling the button here saves the round-trip and surfaces the
    // shortage in the info pane simultaneously.
    const materials = this._recipe ? assessMaterials(this._recipe, this._inventoryProvider(), this._batchCount) : null;
    const insufficient = materials !== null && !materials.sufficient;
    const disabled = this._queuing || insufficient;
    const bx = barW - PAD - QUEUE_BTN_W;
    const by = Math.round((barH - QUEUE_BTN_H) / 2); // contracts:allow-literal

    const c = new Container();
    c.x = bx;
    c.y = by;
    if (!disabled) {
      c.eventMode = "static";
      c.cursor = "pointer";
    }

    const bg = new Graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      bg
        .roundRect(0, 0, QUEUE_BTN_W, QUEUE_BTN_H, 4) // contracts:allow-literal
        .fill({ color: disabled ? BTN_DISABLED : hover ? BTN_HOVER : BTN_BG })
        .stroke({ color: disabled ? MUTED_COLOR : BTN_BORDER, width: 1 }); // contracts:allow-literal
    };
    draw(false);
    c.addChild(bg);

    const label = this._queuing ? "Queuing…" : insufficient ? "Need materials" : "Queue Craft";
    const t = new Text({
      text: label,
      style: {
        fontSize: BODY_SIZE,
        fill: disabled ? MUTED_COLOR : BTN_TEXT,
        fontFamily: "monospace",
        fontWeight: disabled ? "normal" : "bold",
      },
    });
    t.x = Math.round((QUEUE_BTN_W - t.width) / 2); // contracts:allow-literal
    t.y = Math.round((QUEUE_BTN_H - t.height) / 2); // contracts:allow-literal
    c.addChild(t);

    if (!disabled) {
      c.on("pointerover", () => draw(true));
      c.on("pointerout", () => draw(false));
      c.on("pointertap", () => {
        if (!this._recipe || !this.onQueueCraft) return;
        this.onQueueCraft(this._recipe.id, this._batchCount);
        this._queuing = true;
        this.rebuild();
        setTimeout(() => {
          this._queuing = false;
          this.rebuild();
        }, 2000); // contracts:allow-literal — 2s acknowledgement window before re-enabling
      });
    }

    parent.addChild(c);
  }
}
